import { useCallback, useEffect, useMemo, useState } from "react";
import { HeadsUpContext } from "./use-heads-up-context";
import type {
  HeadsUpContentVisibility,
  HeadsUpSidebarModel,
} from "@/components/heads-up/sidebar/types/heads-up-sidebar.types";
import type { HeadsUpContextProviderProps } from "./heads-up-context.types";

export function HeadsUpContextProvider({ model, children }: HeadsUpContextProviderProps) {
  const [cardVisibleCounts, setCardVisibleCounts] = useState<Record<string, number>>({});

  const getCardVisibleCount = useCallback<HeadsUpContentVisibility["get"]>(
    cardId => cardVisibleCounts[cardId],
    [cardVisibleCounts]
  );

  const setCardVisibleCount = useCallback<HeadsUpContentVisibility["set"]>((cardId, updater) => {
    setCardVisibleCounts(current => {
      const currentValue = current[cardId];
      const nextValue = updater(currentValue);
      if (currentValue === nextValue) {
        return current;
      }

      return {
        ...current,
        [cardId]: nextValue,
      };
    });
  }, []);

  useEffect(() => {
    setCardVisibleCounts({});
  }, [model.context.contextId]);

  useEffect(() => {
    const allowedIds = new Set(model.content.cards.map(card => card.id));
    setCardVisibleCounts(current => {
      if (allowedIds.size === 0) return current;
      let didChange = false;
      const next: Record<string, number> = {};

      for (const [cardId, value] of Object.entries(current)) {
        if (allowedIds.has(cardId)) {
          next[cardId] = value;
        } else {
          didChange = true;
        }
      }

      return didChange ? next : current;
    });
  }, [model.content.cards]);

  const visibility = useMemo<HeadsUpContentVisibility>(
    () => ({
      get: getCardVisibleCount,
      set: setCardVisibleCount,
    }),
    [getCardVisibleCount, setCardVisibleCount]
  );

  const enhancedModel = useMemo<HeadsUpSidebarModel>(
    () => ({
      ...model,
      content: {
        ...model.content,
        visibility,
      },
    }),
    [model, visibility]
  );

  return <HeadsUpContext.Provider value={enhancedModel}>{children}</HeadsUpContext.Provider>;
}
